import { formatDate } from "@/lib/utils";
import React, { useState, useMemo } from "react";
import { useSelector } from "react-redux";
import CountUp from "react-countup";
import { DataTable } from "@/components/ui/data-table";
import { API_ENDPOINTS } from "../../../api/header";
import apiClient from "../../../api/apiClient";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";
import { all_routes } from "../../../Router/all_routes";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  PlusCircle,
  MoreVertical,
  Settings,
  Play,
  CheckCircle,
  PackageCheck,
  Ban,
  Edit,
  Trash2,
  Eye,
  Loader2,
  DollarSign,
  Activity,
  ListTodo,
  Percent,
  FileText,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table as ShadcnTable,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  PageHeader,
  PageHeaderHeading,
  PageHeaderActions,
} from "@/components/ui/page-header";
import { FilterCard, FilterSearch } from "@/components/ui/filter-card";
import { TableCard } from "@/components/ui/table-card";
import { PageContainer } from "@/components/layout/PageContainer";

const AssembleJobsList = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const currentStore = useSelector((state) => state.current_store);
  const store_id = currentStore?.id || null;
  const permissions = useSelector((state) => state.userpermission_data) || [];
  const hasPermission = (permission) => permissions.includes(permission);

  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewJobId, setViewJobId] = useState(null);
  const [viewJobBasic, setViewJobBasic] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    description: "",
    onConfirm: () => { },
    variant: "destructive",
    confirmText: "Confirm",
  });

  const currency = useSelector(
    (state) => state.current_store && state.current_store.default_currency,
  );
  const currencySymbol = currency?.symbol || "$";

  const { data: jobsData, isLoading } = useQuery({
    queryKey: ["assemble-jobs", currentPage, pageSize, searchText, store_id],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.ASSEMBLE.JOBS, {
        params: {
          page: currentPage,
          per_page: pageSize,
          search: searchText || undefined,
          store_id: store_id,
        },
      });
      return res.data;
    },
  });

  const { data: jobDetailsData, isLoading: isJobDetailsLoading } = useQuery({
    queryKey: ["assemble-job", viewJobId],
    queryFn: async () => {
      const res = await apiClient.get(
        API_ENDPOINTS.ASSEMBLE.JOB_DETAILS(viewJobId),
      );
      return res.data?.data;
    },
    enabled: !!viewJobId,
  });
  const jobDetails = useMemo(() => jobDetailsData, [jobDetailsData]);

  const { data: statsData } = useQuery({
    queryKey: ["assemble-jobs-stats", store_id],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.ASSEMBLE.JOB_STATISTICS, {
        params: { store_id: store_id || undefined },
      });
      return res.data;
    },
  });
  const stats = useMemo(() => statsData || null, [statsData]);

  const jobs = useMemo(() => jobsData?.data || [], [jobsData]);

  const paginationMeta = {
    current_page: jobsData?.meta?.current_page || 1,
    last_page: jobsData?.meta?.last_page || 1,
    per_page: jobsData?.meta?.per_page || 15,
    total: jobsData?.meta?.total || 0,
  };

  const actionMutation = useMutation({
    mutationFn: async ({ id, action }) => {
      let url = "";
      switch (action) {
        case "confirm":
          url = API_ENDPOINTS.ASSEMBLE.JOB_CONFIRM(id);
          break;
        case "start-building":
          url = API_ENDPOINTS.ASSEMBLE.JOB_START_BUILDING(id);
          break;
        case "submit-qc":
          url = API_ENDPOINTS.ASSEMBLE.JOB_SUBMIT_QC(id);
          break;
        case "complete":
          url = API_ENDPOINTS.ASSEMBLE.JOB_COMPLETE(id);
          break;
        case "deliver":
          url = API_ENDPOINTS.ASSEMBLE.JOB_DELIVER(id);
          break;
        case "cancel":
          url = API_ENDPOINTS.ASSEMBLE.JOB_CANCEL(id);
          break;
        default:
          throw new Error("Invalid action");
      }
      return await apiClient.post(url, {});
    },
    onSuccess: (data, variables) => {
      toast.success(`Job ${variables.action.replace("-", " ")} successful`);
      queryClient.invalidateQueries(["assemble-jobs"]);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Action failed");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) =>
      apiClient.delete(API_ENDPOINTS.ASSEMBLE.JOB_DETAILS(id)),
    onSuccess: () => {
      toast.success("Job deleted successfully");
      queryClient.invalidateQueries(["assemble-jobs"]);
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || "Failed to delete job");
    },
  });

  const openConfirm = (title, description, onConfirm, variant = "default", confirmText = "Confirm") => {
    setConfirmDialog({ isOpen: true, title, description, onConfirm, variant, confirmText });
  };

  const handleConfirmJob = (id) =>
    openConfirm(
      "Confirm Job",
      "Are you sure you want to confirm this job? This will lock the job for editing.",
      () => actionMutation.mutate({ id, action: "confirm" }),
    );

  const handleStartBuilding = (id) =>
    openConfirm(
      "Start Building",
      "Are you sure you want to start building this job?",
      () => actionMutation.mutate({ id, action: "start-building" }),
    );

  const handleSubmitQc = (id) =>
    openConfirm(
      "Submit for QC",
      "Are you sure you want to submit this job for quality control?",
      () => actionMutation.mutate({ id, action: "submit-qc" }),
    );

  const handleCompleteJob = (id) =>
    openConfirm(
      "Complete Job",
      "Are you sure you want to mark this job as complete?",
      () => actionMutation.mutate({ id, action: "complete" }),
    );

  const handleDeliverJob = (id) =>
    openConfirm(
      "Deliver Job",
      "Are you sure you want to mark this job as delivered?",
      () => actionMutation.mutate({ id, action: "deliver" }),
    );

  const handleCancelJob = (id) =>
    openConfirm(
      "Cancel Job",
      "Are you sure you want to cancel this job? This action cannot be undone.",
      () => actionMutation.mutate({ id, action: "cancel" }),
      "destructive",
      "Cancel Job",
    );

  const handleDeleteJob = (id) =>
    openConfirm(
      "Delete Job",
      "Are you sure you want to delete this job? This action cannot be undone.",
      () => deleteMutation.mutate(id),
      "destructive",
      "Delete",
    );


  const handleViewJob = (job) => {
    setViewJobId(job.id);
    setViewJobBasic(job);
    setViewModalOpen(true);
  };

  const getStatusBadge = (statusObj) => {
    const value =
      typeof statusObj === "object" && statusObj !== null
        ? statusObj.value
        : statusObj;
    const label =
      typeof statusObj === "object" && statusObj !== null && statusObj.label
        ? statusObj.label
        : (value || "").charAt(0).toUpperCase() + (value || "").slice(1);

    switch (value) {
      case "draft":
        return <Badge variant="secondary">{label}</Badge>;
      case "confirmed":
        return <Badge variant="default">{label}</Badge>;
      case "building":
        return <Badge variant="warning">{label}</Badge>;
      case "qc":
        return <Badge variant="warning">{label}</Badge>;
      case "completed":
        return <Badge variant="success">{label}</Badge>;
      case "delivered":
        return <Badge variant="success">{label}</Badge>;
      case "cancelled":
        return <Badge variant="destructive">{label}</Badge>;
      default:
        return <Badge variant="outline">{label}</Badge>;
    }
  };

  const columns = [
    {
      accessorKey: "job_number",
      header: "Job Number",
      cell: ({ row }) => (
        <span className="font-medium">{row.original.job_number}</span>
      ),
    },
    {
      accessorKey: "name",
      header: "Job Name",
    },
    {
      accessorKey: "customer",
      header: "Customer",
      cell: ({ row }) =>
        row.original.customer?.full_name ||
        row.original.customer?.company ||
        "-",
    },
    {
      accessorKey: "total_price",
      header: "Total Price",
      cell: ({ row }) =>
        `${currencySymbol}${parseFloat(row.original.total_price || 0).toFixed(2)}`,
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => getStatusBadge(row.original.status),
    },
    {
      accessorKey: "created_at",
      header: "Created At",
      cell: ({ row }) => {
        const date = new Date(row.original.created_at);
        return formatDate(date);
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const statusObj = row.original.status;
        const status =
          typeof statusObj === "object" && statusObj !== null
            ? statusObj.value
            : statusObj;
        const id = row.original.id;
        const isActionDisabled = actionMutation.isPending;

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleViewJob(row.original)}>
                <Eye className="mr-2 h-4 w-4" /> View Details
              </DropdownMenuItem>
              {status === "draft" && (
                <>
                  {hasPermission("assemble job update") && (
                    <Link
                      to={(
                        all_routes.editAssembleJob || "/edit-assemble-job/:id"
                      ).replace(":id", id)}
                    >
                      <DropdownMenuItem>
                        <Edit className="mr-2 h-4 w-4" /> Edit Job
                      </DropdownMenuItem>
                    </Link>
                  )}
                  {hasPermission("assemble job process") && (
                    <DropdownMenuItem
                      disabled={isActionDisabled}
                      onClick={() => handleConfirmJob(id)}
                    >
                      <CheckCircle className="mr-2 h-4 w-4" /> Confirm Job
                    </DropdownMenuItem>
                  )}
                  {hasPermission("assemble job delete") && (
                    <DropdownMenuItem
                      className="text-destructive cursor-pointer"
                      disabled={isActionDisabled}
                      onClick={() => handleDeleteJob(id)}
                    >
                      <Trash2 className="mr-2 h-4 w-4" /> Delete Job
                    </DropdownMenuItem>
                  )}
                </>
              )}
              {status === "confirmed" &&
                hasPermission("assemble job process") && (
                  <DropdownMenuItem
                    disabled={isActionDisabled}
                    onClick={() => handleStartBuilding(id)}
                  >
                    <Play className="mr-2 h-4 w-4" /> Start Building
                  </DropdownMenuItem>
                )}
              {status === "building" &&
                hasPermission("assemble job process") && (
                  <DropdownMenuItem
                    disabled={isActionDisabled}
                    onClick={() => handleSubmitQc(id)}
                  >
                    <Settings className="mr-2 h-4 w-4" /> Submit QC
                  </DropdownMenuItem>
                )}
              {status === "qc" && hasPermission("assemble job process") && (
                <DropdownMenuItem
                  disabled={isActionDisabled}
                  onClick={() => handleCompleteJob(id)}
                >
                  <CheckCircle className="mr-2 h-4 w-4" /> Complete Job
                </DropdownMenuItem>
              )}
              {status === "completed" &&
                hasPermission("assemble job process") && (
                  <>
                    <DropdownMenuItem
                      disabled={isActionDisabled}
                      onClick={() => handleDeliverJob(id)}
                    >
                      <PackageCheck className="mr-2 h-4 w-4" /> Deliver Job
                    </DropdownMenuItem>
                  </>
                )}
              {row.original.can_convert_to_sale &&
                hasPermission("assemble job process") && (
                  <DropdownMenuItem
                    onClick={() =>
                      navigate(
                        all_routes.convertAssembleJobToSale.replace(":id", id),
                      )
                    }
                  >
                    <PackageCheck className="mr-2 h-4 w-4" /> Convert to Sale
                  </DropdownMenuItem>
                )}
              {(row.original.can_convert_to_quotation ??
                row.original.can_convert_to_sale) &&
                hasPermission("assemble job process") && (
                  <DropdownMenuItem
                    onClick={() =>
                      navigate(
                        all_routes.convertAssembleJobToQuotation.replace(
                          ":id",
                          id,
                        ),
                      )
                    }
                  >
                    <FileText className="mr-2 h-4 w-4" /> Convert to Quotation
                  </DropdownMenuItem>
                )}
              {status !== "cancelled" &&
                status !== "delivered" &&
                hasPermission("assemble job process") && (
                  <DropdownMenuItem
                    disabled={isActionDisabled}
                    onClick={() => handleCancelJob(id)}
                    className="text-destructive"
                  >
                    <Ban className="mr-2 h-4 w-4" /> Cancel Job
                  </DropdownMenuItem>
                )}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  return (
    <PageContainer>
      <PageHeader>
        <PageHeaderHeading
          title="Assemble Jobs"
          subtitle="Manage PC assembly jobs"
        />
        <PageHeaderActions>
          {hasPermission("assemble job create") && (
            <Link to={all_routes.addAssembleJob}>
              <Button>
                <PlusCircle className="mr-2 h-4 w-4" /> Add Assemble Job
              </Button>
            </Link>
          )}
        </PageHeaderActions>
      </PageHeader>

      {/* Statistics Overview */}
      {stats && hasPermission("assemble job read") && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Jobs</CardTitle>
              <ListTodo className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                <CountUp start={0} end={stats.total_jobs || 0} duration={1.5} />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Jobs</CardTitle>
              <Activity className="h-4 w-4 text-warning" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                <CountUp
                  start={0}
                  end={stats.active_jobs || 0}
                  duration={1.5}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Completion Rate
              </CardTitle>
              <Percent className="h-4 w-4 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                <CountUp
                  start={0}
                  end={stats.completion_rate || 0}
                  duration={1.5}
                  suffix="%"
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Total Revenue
              </CardTitle>
              <DollarSign className="h-4 w-4 text-success" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-success">
                <CountUp
                  start={0}
                  end={stats.total_revenue || 0}
                  duration={1.5}
                  prefix={currencySymbol}
                  decimals={2}
                />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <FilterCard>
        <FilterSearch
          placeholder="Search jobs..."
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
            setCurrentPage(1);
          }}
        />
      </FilterCard>

      <TableCard>
        <DataTable
          columns={columns}
          data={jobs}
          onRowClick={handleViewJob}
          loading={isLoading}
          pagination={paginationMeta}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
        />
      </TableCard>

      <Dialog
        open={viewModalOpen}
        onOpenChange={(open) => {
          setViewModalOpen(open);
          if (!open) {
            setViewJobId(null);
            setViewJobBasic(null);
          }
        }}
      >
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col p-0">
          <DialogHeader className="px-6 py-4 border-b">
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-muted-foreground" />
              Job Details - {viewJobBasic?.job_number}
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="flex-1 px-6 py-4">
            {isJobDetailsLoading ? (
              <div className="flex justify-center p-8">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : jobDetails ? (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">
                        Job Information
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Job Name:</span>
                        <span className="font-medium">{jobDetails.name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Status:</span>
                        {getStatusBadge(jobDetails.status)}
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          Created Date:
                        </span>
                        <span className="font-medium">
                          {formatDate(jobDetails.created_at)}
                        </span>
                      </div>
                      {jobDetails.delivered_at && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">
                            Delivered Date:
                          </span>
                          <span className="font-medium">
                            {formatDate(jobDetails.delivered_at,)}
                          </span>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">
                        Customer Details
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Name:</span>
                        <span className="font-medium">
                          {jobDetails.customer
                            ? jobDetails.customer.company ||
                            jobDetails.customer.full_name
                            : "-"}
                        </span>
                      </div>
                      {jobDetails.customer && (
                        <>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">
                              Email:
                            </span>
                            <span className="font-medium">
                              {jobDetails.customer.email || "-"}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">
                              Phone:
                            </span>
                            <span className="font-medium">
                              {jobDetails.customer.phone || "-"}
                            </span>
                          </div>
                        </>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {jobDetails.items && jobDetails.items.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Components</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="rounded-md border overflow-x-auto">
                        <ShadcnTable>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="w-12">#</TableHead>
                              <TableHead>Product</TableHead>
                              <TableHead className="text-right">Qty</TableHead>
                              <TableHead className="text-right">
                                Unit Price
                              </TableHead>
                              <TableHead className="text-right">
                                Total
                              </TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {jobDetails.items.map((item, index) => {
                              return (
                                <TableRow key={item.id}>
                                  <TableCell className="font-medium">
                                    {index + 1}
                                  </TableCell>
                                  <TableCell>
                                    <div className="font-medium">
                                      {item.product?.name ||
                                        item.product_data?.name ||
                                        "Unknown Product"}
                                    </div>
                                    {item.product?.sku && (
                                      <div className="text-[11px] text-muted-foreground mt-0.5">
                                        SKU: {item.product.sku}
                                      </div>
                                    )}
                                    {item.inventory_serial_id && (
                                      <div className="text-[11px] text-muted-foreground mt-0.5">
                                        Serial Tracked
                                      </div>
                                    )}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    {item.quantity}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    {currencySymbol}
                                    {parseFloat(item.unit_price || 0).toFixed(
                                      2,
                                    )}
                                  </TableCell>
                                  <TableCell className="text-right font-semibold">
                                    {currencySymbol}
                                    {parseFloat(item.total_price || 0).toFixed(
                                      2,
                                    )}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </ShadcnTable>
                      </div>
                    </CardContent>
                  </Card>
                )}

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">
                      Financial Summary
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        Components Total:
                      </span>
                      <span className="font-medium">
                        {currencySymbol}
                        {parseFloat(
                          jobDetails.items?.reduce(
                            (sum, item) =>
                              sum + parseFloat(item.total_price || 0),
                            0,
                          ) || 0,
                        ).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        Assembly Fee:
                      </span>
                      <span className="font-medium">
                        {currencySymbol}
                        {parseFloat(jobDetails.assembly_fee || 0).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between pt-2 border-t mt-2">
                      <span className="font-bold">Grand Total:</span>
                      <span className="font-bold text-primary">
                        {currencySymbol}
                        {parseFloat(jobDetails.total_price || 0).toFixed(2)}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ) : null}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={confirmDialog.isOpen}
        onOpenChange={(open) =>
          setConfirmDialog((prev) => ({ ...prev, isOpen: open }))
        }
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmDialog.title}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDialog.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() =>
                setConfirmDialog((prev) => ({ ...prev, isOpen: false }))
              }
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className={
                confirmDialog.variant === "destructive"
                  ? "bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  : ""
              }
              onClick={() => {
                confirmDialog.onConfirm();
                setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
              }}
            >
              {confirmDialog.confirmText || "Confirm"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  );
};

export default AssembleJobsList;
